import { CanvasActionContext } from "@/components/canvas/canvasContext.tsx";
import { CanvasLayers } from "@/components/canvas/CanvasLayers.tsx";
import { CanvasNavBoundary } from "@/components/canvas/CanvasNavBoundary.tsx";
import { useEphemeralCanvas } from "@/components/canvas/useEphemeralCanvas.ts";
import {
  MainContentWrapper,
  SiteHeaderContent,
  ViewWrapper,
} from "@/components/layout/ViewWrapper.tsx";
import { ThreadParticipantsBody } from "@/components/contacts/ThreadParticipantsBody.tsx";
import { parseThreadId } from "@/components/threads/util.ts";
import { useIsMobile } from "@/hooks/use-mobile.tsx";
import { useParams } from "wouter";

/**
 * Full-page surface for a thread's participant roster — where a facepile leads
 * on mobile, and where a cmd-click or a cold deep link lands on desktop. Reuses
 * the shared {@link ThreadParticipantsBody} the canvas card renders, so the list
 * is identical across surfaces; only the chrome differs.
 *
 * The page carries its own canvas, so opening a participant here presents their
 * contact card over the roster rather than in the right sidebar — the same
 * in-place drill the facepile gives on a canvas page. It is the modal stack
 * alone ({@link CanvasLayers}, not `CanvasHost`): the calendar the canvas
 * usually rests on has nothing to do with this screen, and mounting one behind
 * the roster would be noise.
 *
 * Canvas content here is ephemeral, like home's — this page holds no thread of
 * its own to persist a stack against.
 */
export const ThreadParticipants = () => {
  const { threadId } = useParams<{ threadId: string }>();
  const numericThreadId = parseThreadId(threadId);

  // Mobile gets real pages rather than panels, matching the thread and calendar
  // screens: with no canvas provided, a participant link falls through to the
  // contact's own page.
  const isMobile = useIsMobile();
  const showCanvas = !isMobile;
  const { canvasStack, isCanvasOpen, isFullscreen, canvasActions } = useEphemeralCanvas();

  return (
    <CanvasActionContext.Provider value={showCanvas ? canvasActions : undefined}>
      <ViewWrapper headerContent={<SiteHeaderContent includeBackButton title="People" />}>
        {/* The roster is this page's content rather than a card, so the canvas
            rests empty and a participant drills in as the stack's first layer. */}
        <CanvasNavBoundary>
          {/* Positioned, so a card covers and dims the roster but leaves the
              page header — and the back button out of here — reachable. */}
          <div className="relative flex flex-1 min-h-0 flex-col">
            <MainContentWrapper columns={7} className="py-4">
              {numericThreadId !== undefined ? (
                <ThreadParticipantsBody threadId={numericThreadId} />
              ) : (
                <p className="p-4 text-muted-foreground text-sm">Thread not found.</p>
              )}
            </MainContentWrapper>
            {showCanvas && (
              <CanvasLayers
                canvasStack={canvasStack}
                isCanvasOpen={isCanvasOpen}
                isFullscreen={isFullscreen}
              />
            )}
          </div>
        </CanvasNavBoundary>
      </ViewWrapper>
    </CanvasActionContext.Provider>
  );
};
